/**
 * Layer 13: DDoS Fortress — Mathematical DDoS Defense System (POLISHED)
 * Armadillo Adamantium Next
 *
 * Polish additions:
 * - Edge prefiltering (fast rejection before heavy math)
 * - Route-specific pressure thresholds
 * - Attack-mode gating (escalating response tiers)
 * - Multi-CDN fallback awareness
 */

// ============================================
// 1. SHANNON ENTROPY ANALYSIS
// ============================================
// H(X) = -Σ p(x) * log₂(p(x))
// Normal traffic: high entropy (diverse requests, UAs, paths)
// DDoS traffic: low entropy (uniform requests from botnet)

interface EntropyWindow {
  timestamps: number[];
  paths: string[];
  userAgents: string[];
  ips: string[];
}

const ENTROPY_WINDOW_SIZE = 100; // Analyze last 100 requests
const entropyBuffer: EntropyWindow = {
  timestamps: [],
  paths: [],
  userAgents: [],
  ips: [],
};

/**
 * Compute Shannon entropy of a discrete distribution
 * Returns value in [0, log₂(n)] where n = unique items
 * Normalized to [0, 1] by dividing by log₂(n)
 */
function shannonEntropy(items: string[]): number {
  if (items.length === 0) return 1; // Empty = assume normal
  if (items.length === 1) return 0; // Single item = zero entropy

  const freq = new Map<string, number>();
  for (const item of items) {
    freq.set(item, (freq.get(item) || 0) + 1);
  }

  const n = items.length;
  let entropy = 0;

  for (const count of freq.values()) {
    const p = count / n;
    if (p > 0) {
      entropy -= p * Math.log2(p);
    }
  }

  // Normalize by maximum possible entropy (log₂ of unique items)
  const maxEntropy = Math.log2(freq.size);
  if (maxEntropy === 0) return 0;

  return entropy / maxEntropy; // [0, 1] where 0 = uniform, 1 = maximally diverse
}

/**
 * Add request to entropy tracking buffer
 */
export function recordRequestEntropy(
  ip: string,
  path: string,
  userAgent: string
): void {
  entropyBuffer.timestamps.push(Date.now());
  entropyBuffer.paths.push(path);
  entropyBuffer.userAgents.push(userAgent);
  entropyBuffer.ips.push(ip);

  // Trim to window size
  while (entropyBuffer.timestamps.length > ENTROPY_WINDOW_SIZE) {
    entropyBuffer.timestamps.shift();
    entropyBuffer.paths.shift();
    entropyBuffer.userAgents.shift();
    entropyBuffer.ips.shift();
  }
}

/**
 * Compute multi-dimensional entropy score
 * Combines path entropy, UA entropy, IP entropy, and timing entropy
 * Low combined entropy = likely DDoS
 */
export function computeTrafficEntropy(): {
  pathEntropy: number;
  uaEntropy: number;
  ipEntropy: number;
  timingEntropy: number;
  combinedScore: number; // 0 = DDoS, 1 = normal
} {
  const pathEntropy = shannonEntropy(entropyBuffer.paths);
  const uaEntropy = shannonEntropy(entropyBuffer.userAgents);
  const ipEntropy = shannonEntropy(entropyBuffer.ips);

  // Timing entropy: compute inter-arrival times and measure their variance
  const timingEntropy = computeTimingEntropy();

  // Weighted combination (timing is hardest to fake)
  // Weights: path 0.2, UA 0.15, IP 0.25, timing 0.4
  const combinedScore =
    pathEntropy * 0.2 +
    uaEntropy * 0.15 +
    ipEntropy * 0.25 +
    timingEntropy * 0.4;

  return { pathEntropy, uaEntropy, ipEntropy, timingEntropy, combinedScore };
}

/**
 * Compute entropy of inter-arrival times
 * Bots send at uniform intervals (low entropy)
 * Humans send at random intervals (high entropy)
 */
function computeTimingEntropy(): number {
  if (entropyBuffer.timestamps.length < 10) return 0.5;

  // Compute inter-arrival times
  const intervals: string[] = [];
  for (let i = 1; i < entropyBuffer.timestamps.length; i++) {
    const delta = entropyBuffer.timestamps[i] - entropyBuffer.timestamps[i - 1];
    // Quantize into buckets: <50ms, 50-200ms, 200-1000ms, 1-5s, >5s
    let bucket: string;
    if (delta < 50) bucket = 'A';
    else if (delta < 200) bucket = 'B';
    else if (delta < 1000) bucket = 'C';
    else if (delta < 5000) bucket = 'D';
    else bucket = 'E';
    intervals.push(bucket);
  }

  return shannonEntropy(intervals);
}

// ============================================
// 2. COUNT-MIN SKETCH — Heavy Hitter Detection
// ============================================
// Memory-efficient probabilistic data structure
// Tracks frequency of items (IPs) using O(d × w) space
// Never underestimates count, may slightly overestimate

const CMS_DEPTH = 4; // 4 independent hash functions
const CMS_WIDTH = 1024; // 1024 counters per function
const cmsTable: number[][] = Array.from({ length: CMS_DEPTH }, () =>
  new Array(CMS_WIDTH).fill(0)
);

// Periodic decay to prevent table saturation
let cmsTotalCount = 0;
const CMS_DECAY_THRESHOLD = 50000; // Decay after 50K updates

/**
 * Simple string hash → integer in [0, width)
 * Uses different seeds for each row
 */
function cmsHash(item: string, seed: number, width: number): number {
  let h = seed;
  for (let i = 0; i < item.length; i++) {
    h = ((h << 5) - h + item.charCodeAt(i)) | 0;
  }
  // Additional mixing (MurmurHash3 finalizer)
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return Math.abs(h) % width;
}

/**
 * Record a request from an IP in the Count-Min Sketch
 */
export function cmsUpdate(ipHash: string): void {
  for (let d = 0; d < CMS_DEPTH; d++) {
    const idx = cmsHash(ipHash, d * 7919 + 31, CMS_WIDTH);
    cmsTable[d][idx]++;
  }
  cmsTotalCount++;

  // Periodic exponential decay (halve all counters)
  if (cmsTotalCount > CMS_DECAY_THRESHOLD) {
    for (let d = 0; d < CMS_DEPTH; d++) {
      for (let w = 0; w < CMS_WIDTH; w++) {
        cmsTable[d][w] = (cmsTable[d][w] / 2) | 0;
      }
    }
    cmsTotalCount = (cmsTotalCount / 2) | 0;
  }
}

/**
 * Query estimated count for an IP
 * Returns minimum across all hash functions (tightest upper bound)
 */
export function cmsQuery(ipHash: string): number {
  let minCount = Infinity;
  for (let d = 0; d < CMS_DEPTH; d++) {
    const idx = cmsHash(ipHash, d * 7919 + 31, CMS_WIDTH);
    minCount = Math.min(minCount, cmsTable[d][idx]);
  }
  return minCount;
}

/**
 * Detect if an IP is a "heavy hitter" (disproportionate traffic share)
 * Returns true if IP's traffic exceeds threshold
 */
export function isHeavyHitter(
  ipHash: string,
  thresholdFraction: number = 0.05
): boolean {
  const ipCount = cmsQuery(ipHash);
  const threshold = Math.max(10, cmsTotalCount * thresholdFraction);
  return ipCount > threshold;
}

// ============================================
// 3. CUSUM CHANGE DETECTION
// ============================================
// Cumulative Sum Control Chart — from statistical quality control
// Detects the instant traffic patterns shift (attack start/end)
// Much faster than moving average comparison

let cusumPositive = 0; // Detects upward shifts (traffic spike)
let cusumNegative = 0; // Detects downward shifts (traffic drop)
const CUSUM_SLACK = 0.5; // Allowable deviation before accumulation
const CUSUM_THRESHOLD = 5.0; // Decision threshold (in std deviations)
let baselineRate = 10; // Requests per minute baseline
let lastCusumUpdate = Date.now();
let requestsSinceLastCusum = 0;

/**
 * Feed a new request into the CUSUM detector
 * Returns true if a significant traffic pattern shift is detected
 */
export function cusumDetect(): { spike: boolean; drop: boolean; severity: number } {
  requestsSinceLastCusum++;
  const now = Date.now();
  const elapsed = now - lastCusumUpdate;

  // Evaluate every 10 seconds
  if (elapsed < 10000) {
    return { spike: false, drop: false, severity: 0 };
  }

  // Compute current rate (requests per minute)
  const currentRate = (requestsSinceLastCusum / elapsed) * 60000;
  requestsSinceLastCusum = 0;
  lastCusumUpdate = now;

  // Normalize: deviation from baseline in units of baseline
  const deviation = (currentRate - baselineRate) / Math.max(1, baselineRate);

  // CUSUM accumulation
  cusumPositive = Math.max(0, cusumPositive + deviation - CUSUM_SLACK);
  cusumNegative = Math.max(0, cusumNegative - deviation - CUSUM_SLACK);

  const spike = cusumPositive > CUSUM_THRESHOLD;
  const drop = cusumNegative > CUSUM_THRESHOLD;

  // Severity: how far above threshold
  const severity = Math.max(
    cusumPositive / CUSUM_THRESHOLD,
    cusumNegative / CUSUM_THRESHOLD
  );

  // If detected, slowly adapt baseline to new normal
  if (spike || drop) {
    baselineRate = baselineRate * 0.7 + currentRate * 0.3;
    cusumPositive = 0;
    cusumNegative = 0;
  } else {
    // Slowly drift baseline toward current rate
    baselineRate = baselineRate * 0.95 + currentRate * 0.05;
  }

  return { spike, drop, severity };
}

// ============================================
// 4. ADAPTIVE MEMORY-HARD PROOF-OF-WORK
// ============================================
// The key innovation: difficulty adapts per-IP based on reputation
// Memory-hard component prevents GPU/ASIC acceleration
// Server verification is O(1) — client does all the work

/**
 * Generate an adaptive PoW challenge
 * Difficulty scales with:
 * - IP reputation (lower rep = harder puzzle)
 * - Current system load (higher load = harder)
 * - Request velocity (faster = harder)
 */
export function generateAdaptivePoW(
  ipHash: string,
  reputation: number, // 0-1, 1 = good
  systemLoad: number, // 0-1
  requestVelocity: number // requests per minute
): {
  challenge: string;
  difficulty: number; // Number of leading zero bits required
  memoryRounds: number; // Sequential hash chain length
  salt: string;
  expiresAt: number;
} {
  // Adaptive difficulty: 8 (easy) to 22 (very hard) bits
  const reputationFactor = (1 - reputation) * 8;
  const loadFactor = systemLoad * 4;
  const velocityFactor = Math.min(requestVelocity / 50, 1) * 4;
  const difficulty = Math.min(22, Math.max(8, Math.round(8 + reputationFactor + loadFactor + velocityFactor)));

  // Memory rounds: 100 (easy) to 10000 (hard) sequential hash iterations
  // This makes GPU parallelism useless — each step depends on the previous
  const memoryRounds = Math.min(10000, Math.max(100, Math.round(100 + (difficulty - 8) * 700)));

  const salt = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  const challenge = `armadillo-pow-v2:${ipHash.substring(0, 16)}:${Date.now()}:${salt}`;

  return {
    challenge,
    difficulty,
    memoryRounds,
    salt,
    expiresAt: Date.now() + 30000, // 30 seconds to solve
  };
}

/**
 * Solve the adaptive PoW challenge (client-side)
 * Memory-hard: must compute sequential hash chain, then find nonce with leading zeros
 */
export async function solveAdaptivePoW(
  challenge: string,
  difficulty: number,
  memoryRounds: number
): Promise<{ nonce: number; memoryProof: string } | null> {
  const encoder = new TextEncoder();

  // Phase 1: Memory-hard chain
  // Compute hash(chain(hash(chain(...)))) for memoryRounds iterations
  // Each step depends on the previous — cannot be parallelized
  let chainHash = challenge;
  for (let i = 0; i < memoryRounds; i++) {
    const data = encoder.encode(chainHash + ':' + i);
    const hash = await crypto.subtle.digest('SHA-256', data);
    chainHash = Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Phase 2: Find nonce that produces required leading zero bits
  const targetBits = difficulty;
  for (let nonce = 0; nonce < 5000000; nonce++) {
    const data = encoder.encode(chainHash + ':' + nonce);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(hash);

    // Count leading zero bits
    let zeroBits = 0;
    for (let i = 0; i < bytes.length; i++) {
      if (bytes[i] === 0) {
        zeroBits += 8;
      } else {
        // Count leading zeros in this byte
        let b = bytes[i];
        while ((b & 0x80) === 0 && zeroBits < targetBits) {
          zeroBits++;
          b <<= 1;
        }
        break;
      }
      if (zeroBits >= targetBits) break;
    }

    if (zeroBits >= targetBits) {
      return { nonce, memoryProof: chainHash };
    }
  }

  return null; // Failed to solve
}

/**
 * Verify adaptive PoW solution (server-side)
 * Fast O(memoryRounds) verification — same chain, but only once
 */
export async function verifyAdaptivePoW(
  challenge: string,
  difficulty: number,
  memoryRounds: number,
  nonce: number,
  memoryProof: string
): Promise<boolean> {
  const encoder = new TextEncoder();

  // Phase 1: Recompute memory-hard chain
  let chainHash = challenge;
  for (let i = 0; i < memoryRounds; i++) {
    const data = encoder.encode(chainHash + ':' + i);
    const hash = await crypto.subtle.digest('SHA-256', data);
    chainHash = Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Verify the chain hash matches
  if (chainHash !== memoryProof) return false;

  // Phase 2: Verify the nonce produces required leading zeros
  const data = encoder.encode(chainHash + ':' + nonce);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(hash);

  let zeroBits = 0;
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === 0) {
      zeroBits += 8;
    } else {
      let b = bytes[i];
      while ((b & 0x80) === 0) {
        zeroBits++;
        b <<= 1;
      }
      break;
    }
    if (zeroBits >= difficulty) break;
  }

  return zeroBits >= difficulty;
}

// ============================================
// 5. KOLMOGOROV COMPLEXITY APPROXIMATION
// ============================================
// Uses compression ratio as proxy for Kolmogorov complexity
// K(x) ≈ length(compress(x))
// Attack traffic has LOW complexity (repetitive patterns)
// Normal traffic has HIGH complexity (diverse content)

/**
 * Estimate complexity of a batch of request payloads
 * Uses a simple run-length encoding as fast compression proxy
 */
export function estimateTrafficComplexity(requestBodies: string[]): number {
  if (requestBodies.length === 0) return 0.5;

  const concatenated = requestBodies.join('|');
  if (concatenated.length < 10) return 0.5;

  // Run-length encoding compression
  let compressed = 0;
  let runLength = 1;

  for (let i = 1; i < concatenated.length; i++) {
    if (concatenated[i] === concatenated[i - 1]) {
      runLength++;
    } else {
      compressed += 1 + (runLength > 1 ? Math.ceil(Math.log10(runLength)) : 0);
      runLength = 1;
    }
  }
  compressed += 1 + (runLength > 1 ? Math.ceil(Math.log10(runLength)) : 0);

  // Compression ratio: 0 = highly compressible (attack), 1 = incompressible (normal)
  const ratio = compressed / concatenated.length;

  // Also measure unique character ratio (vocabulary richness)
  const uniqueChars = new Set(concatenated).size;
  const vocabRichness = uniqueChars / Math.min(256, concatenated.length);

  // Combined complexity score
  return Math.min(1, (ratio * 0.6 + vocabRichness * 0.4) * 2);
}

// ============================================
// 6. INTER-ARRIVAL TIME ANALYSIS
// ============================================
// Bots send requests at machine-precise intervals
// Humans have naturally variable timing
// Coefficient of variation (CV) = stddev / mean
// Low CV = bot, High CV = human

const ipTimingMap = new Map<string, number[]>(); // IP → last 20 inter-arrival times

/**
 * Record request timing for an IP
 * Returns bot likelihood score (0 = human, 1 = bot)
 */
export function analyzeTimingVariance(
  ipHash: string,
  timestamp: number
): { botLikelihood: number; cv: number; regularity: number } {
  let timings = ipTimingMap.get(ipHash);
  if (!timings) {
    timings = [];
    ipTimingMap.set(ipHash, timings);
  }

  if (timings.length > 0) {
    const lastTime = timings[timings.length - 1];
    const interval = timestamp - lastTime;
    if (interval > 0 && interval < 60000) {
      timings.push(interval);
      if (timings.length > 20) timings.shift();
    }
  } else {
    timings.push(timestamp);
  }

  if (timings.length < 5) {
    return { botLikelihood: 0.5, cv: 1, regularity: 0.5 };
  }

  // Compute inter-arrival times
  const intervals: number[] = [];
  for (let i = 1; i < timings.length; i++) {
    intervals.push(timings[i] - timings[i - 1]);
  }

  if (intervals.length < 3) {
    return { botLikelihood: 0.5, cv: 1, regularity: 0.5 };
  }

  // Coefficient of Variation (CV = σ/μ)
  const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const variance =
    intervals.reduce((sum, v) => sum + (v - mean) ** 2, 0) / intervals.length;
  const stddev = Math.sqrt(variance);
  const cv = mean > 0 ? stddev / mean : 0;

  // Regularity: how close intervals are to each other
  // Compute autocorrelation at lag 1
  let autocorrelation = 0;
  for (let i = 1; i < intervals.length; i++) {
    autocorrelation += (intervals[i] - mean) * (intervals[i - 1] - mean);
  }
  autocorrelation /= variance * (intervals.length - 1) || 1;

  // Bot signals:
  // - CV < 0.3 (very uniform timing)
  // - Autocorrelation > 0.7 (predictable pattern)
  // - Mean interval < 100ms (too fast for human)
  let botScore = 0;
  if (cv < 0.3) botScore += 0.4;
  else if (cv < 0.5) botScore += 0.2;
  if (Math.abs(autocorrelation) > 0.7) botScore += 0.3;
  if (mean < 100) botScore += 0.3;
  else if (mean < 500) botScore += 0.1;

  return {
    botLikelihood: Math.min(1, botScore),
    cv,
    regularity: Math.abs(autocorrelation),
  };
}

// ============================================
// COMPOSITE DDoS SCORE
// ============================================

/**
 * Compute overall DDoS threat score for current traffic
 * Combines all mathematical techniques into a single verdict
 */
export function computeDDoSScore(ipHash: string): {
  score: number; // 0 = safe, 1 = active DDoS
  signals: {
    entropy: number;
    heavyHitter: boolean;
    cusumSpike: boolean;
    botTiming: number;
    complexity: number;
  };
  recommendedAction: 'allow' | 'challenge' | 'throttle' | 'block';
  powDifficulty: number; // Recommended PoW difficulty
} {
  const entropy = computeTrafficEntropy();
  const heavyHitter = isHeavyHitter(ipHash);
  const cusum = cusumDetect();
  const timing = analyzeTimingVariance(ipHash, Date.now());

  // Each signal contributes to the score
  let score = 0;

  // Low entropy = DDoS signal (weight: 0.25)
  const entropySignal = 1 - entropy.combinedScore;
  score += entropySignal * 0.25;

  // Heavy hitter = DDoS signal (weight: 0.20)
  score += heavyHitter ? 0.20 : 0;

  // CUSUM spike = DDoS signal (weight: 0.20)
  const cusumSignal = cusum.spike ? Math.min(1, cusum.severity / 3) : 0;
  score += cusumSignal * 0.20;

  // Bot timing = DDoS signal (weight: 0.20)
  score += timing.botLikelihood * 0.20;

  // IP request velocity from CMS (weight: 0.15)
  const cmsCount = cmsQuery(ipHash);
  const velocitySignal = Math.min(1, cmsCount / 100);
  score += velocitySignal * 0.15;

  score = Math.min(1, Math.max(0, score));

  // Determine recommended action
  let recommendedAction: 'allow' | 'challenge' | 'throttle' | 'block';
  let powDifficulty: number;

  if (score < 0.3) {
    recommendedAction = 'allow';
    powDifficulty = 0;
  } else if (score < 0.5) {
    recommendedAction = 'challenge';
    powDifficulty = 10;
  } else if (score < 0.75) {
    recommendedAction = 'throttle';
    powDifficulty = 16;
  } else {
    recommendedAction = 'block';
    powDifficulty = 22;
  }

  return {
    score,
    signals: {
      entropy: entropy.combinedScore,
      heavyHitter,
      cusumSpike: cusum.spike,
      botTiming: timing.botLikelihood,
      complexity: entropySignal,
    },
    recommendedAction,
    powDifficulty,
  };
}

// ============================================
// POLISH: EDGE PREFILTER & ROUTE THRESHOLDS
// ============================================

/**
 * Fast prefilter: reject obviously malicious requests before heavy math
 * Runs in O(1) — checks only cached state
 */
const prefilterBlacklist = new Map<string, { blockedAt: number; ttl: number }>();
const PREFILTER_MAX_SIZE = 20000;

export function edgePrefilterCheck(ipHash: string): { blocked: boolean; reason?: string } {
  const entry = prefilterBlacklist.get(ipHash.slice(0, 16));
  if (entry && Date.now() - entry.blockedAt < entry.ttl) {
    return { blocked: true, reason: 'IP in prefilter blacklist' };
  }
  if (entry && Date.now() - entry.blockedAt >= entry.ttl) {
    prefilterBlacklist.delete(ipHash.slice(0, 16));
  }
  return { blocked: false };
}

export function edgePrefilterAdd(ipHash: string, ttlMs: number = 600000): void {
  if (prefilterBlacklist.size > PREFILTER_MAX_SIZE) {
    const now = Date.now();
    for (const [key, entry] of prefilterBlacklist) {
      if (now - entry.blockedAt > entry.ttl) prefilterBlacklist.delete(key);
    }
  }
  prefilterBlacklist.set(ipHash.slice(0, 16), { blockedAt: Date.now(), ttl: ttlMs });
}

/**
 * Route-specific DDoS pressure thresholds
 * Critical routes activate defenses at lower pressure
 */
const routePressureThresholds: Record<string, number> = {
  '/api/admin': 0.2,
  '/api/stripe': 0.3,
  '/api/payment': 0.3,
  '/api/auth': 0.4,
  '/api/bounties': 0.5,
  '/': 0.6,
  '_default': 0.5,
};

export function getRoutePressureThreshold(path: string): number {
  for (const [pattern, threshold] of Object.entries(routePressureThresholds)) {
    if (pattern !== '_default' && path.startsWith(pattern)) return threshold;
  }
  return routePressureThresholds['_default'];
}

/**
 * Attack-mode gating: escalating response tiers based on pressure
 */
export type AttackGateMode = 'normal' | 'challenge' | 'throttle' | 'block' | 'blackhole';

export function computeAttackGateMode(pressure: number, routeThreshold: number): AttackGateMode {
  if (pressure < routeThreshold * 0.5) return 'normal';
  if (pressure < routeThreshold) return 'challenge';
  if (pressure < routeThreshold * 1.5) return 'throttle';
  if (pressure < routeThreshold * 2.0) return 'block';
  return 'blackhole';
}
